import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { EASE, Reveal } from '../components/motion.tsx';
import { Button, Callout, Chip, PageHead, Panel, Skeleton, useToast } from '../components/ui.tsx';
import { ApiError, get, post } from '../lib/api.ts';
import { minutes, time } from '../lib/format.ts';
import { captureJpeg, distanceM, isFrontal, loadLandmarker, readSignal, STEP_PROMPT, stepDetector, type Step } from '../lib/liveness.ts';
import { useMe } from '../lib/session.ts';

interface Status {
  today: string;
  timeZone: string;
  office: { name: string; geofence: { lat: number; lng: number; radiusM: number } | null; hasNetworks: boolean };
  /** Whether this connection is one of the office's networks (null: it has none). Submit checks again. */
  onOfficeNetwork: boolean | null;
  consent: { grantedAt: string; current: boolean } | null;
  /** current: false when the server's face model changed since enrolment (enrol again). */
  enrolled: { at: string; model: string; current: boolean | null } | null;
  timesheet: { clockInAt: string | null; clockOutAt: string | null; lateMinutes: number; shiftStartAt: string | null; shiftEndAt: string | null } | null;
  next: 'in' | 'out';
  matcher: 'mock' | 'http';
}

function useCamera() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [on, setOn] = useState(false);
  const start = useCallback(async () => {
    setError(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 960 }, height: { ideal: 720 } }, audio: false });
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play();
      }
      setOn(true);
    } catch (e) {
      setError((e as Error).name === 'NotAllowedError' ? 'Camera access was blocked. Allow it in your browser’s site settings, then try again.' : 'No camera is available on this device.');
    }
  }, []);
  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setOn(false);
  }, []);
  useEffect(() => stop, [stop]);
  return { videoRef, start, stop, on, error };
}

type GeoState = 'pending' | 'ok' | 'denied' | 'unavailable' | 'unsupported';

function useLocation(enabled: boolean) {
  const [loc, setLoc] = useState<{ lat: number; lng: number; accuracy: number } | null>(null);
  const [state, setState] = useState<GeoState>('pending');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    if (!('geolocation' in navigator)) {
      setState('unsupported');
      return;
    }
    setState((st) => (st === 'ok' ? st : 'pending'));
    const id = navigator.geolocation.watchPosition(
      (p) => {
        setLoc({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy });
        setState('ok');
      },
      (e) => {
        if (e.code === 1) {
          setLoc(null);
          setState('denied');
        } else setState((st) => (st === 'ok' ? st : 'unavailable'));
      },
      { enableHighAccuracy: true, maximumAge: 30_000, timeout: 15_000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [enabled, attempt]);
  return { loc, state, retry: () => setAttempt((a) => a + 1) };
}

export function CheckIn() {
  const me = useMe().data!;
  const qc = useQueryClient();
  const toast = useToast();
  const status = useQuery({ queryKey: ['checkin-status'], queryFn: () => get<Status>('/api/checkin/status'), enabled: !!me.employee });
  const cam = useCamera();
  const geo = useLocation(!!status.data);
  const [phase, setPhase] = useState<'idle' | 'enrolling' | 'challenge' | 'submitting' | 'done'>('idle');
  const [steps, setSteps] = useState<Step[]>([]);
  const [stepIdx, setStepIdx] = useState(0);
  const [framed, setFramed] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [modelError, setModelError] = useState<string | null>(null);
  const [result, setResult] = useState<{ at: string; direction: 'in' | 'out'; score: number; withinGeofence: boolean | null; placeCheck: 'geofence' | 'network' | null; lateMinutes?: number; devMatcher: boolean } | null>(null);
  const [now, setNow] = useState(Date.now());
  const loop = useRef<number>(0);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => () => cancelAnimationFrame(loop.current), []);

  const s = status.data;
  const tz = s?.timeZone ?? me.employee?.timeZone ?? 'Asia/Makassar';
  const dist = s?.office.geofence && geo.loc ? distanceM(geo.loc, s.office.geofence) : null;
  const inside = dist !== null && s?.office.geofence ? dist - Math.min(geo.loc!.accuracy, 100) <= s.office.geofence.radiusM : null;
  // The server's place rule, ahead of time: inside the geofence, or on one of the office's networks.
  const fenced = !!s?.office.geofence;
  const onNetwork = s?.onOfficeNetwork === true;
  const placeNeeded = !!s && (fenced || s.office.hasNetworks) && !onNetwork;
  const placeBlock: 'waiting' | 'no-location' | 'off-network' | null = !placeNeeded ? null : !fenced ? 'off-network' : geo.state === 'pending' ? 'waiting' : geo.state === 'ok' ? null : 'no-location';
  const recheck = () => {
    geo.retry();
    void status.refetch();
  };

  const consent = async () => {
    await post('/api/checkin/consent', { accept: true });
    void qc.invalidateQueries({ queryKey: ['checkin-status'] });
  };

  const enroll = async () => {
    setProblem(null);
    setPhase('enrolling');
    await cam.start();
    await new Promise((r) => setTimeout(r, 900));
    const v = cam.videoRef.current;
    if (!v || !v.videoWidth) {
      setPhase('idle');
      return;
    }
    const frames: string[] = [];
    for (let i = 0; i < 3; i++) {
      frames.push(captureJpeg(v));
      await new Promise((r) => setTimeout(r, 450));
    }
    try {
      await post('/api/checkin/enroll', { frames });
      toast({ title: 'Face enrolled', body: 'You can check in with your face from now on.' });
      void qc.invalidateQueries({ queryKey: ['checkin-status'] });
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : 'Enrolment failed.');
    } finally {
      cam.stop();
      setPhase('idle');
    }
  };

  const submit = async (ch: { id: string; nonce: string; steps: Step[] }, evidence: { steps: { step: Step; passedAt: number }[]; frames: number; durationMs: number }, frontal?: string) => {
    setPhase('submitting');
    const v = cam.videoRef.current!;
    const frame = frontal ?? captureJpeg(v);
    try {
      const r = await post<{ at: string; direction: 'in' | 'out'; score: number; withinGeofence: boolean | null; placeCheck: 'geofence' | 'network' | null; timesheet: { lateMinutes: number } | null; devMatcher: boolean }>('/api/checkin/submit', {
        challengeId: ch.id,
        direction: s!.next,
        frame,
        evidence: { nonce: ch.nonce, ...evidence },
        location: geo.loc,
      });
      setResult({ ...r, lateMinutes: r.timesheet?.lateMinutes });
      setPhase('done');
      void qc.invalidateQueries({ queryKey: ['checkin-status'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : 'Check-in failed.');
      setPhase('idle');
      // The face model changed: reload the status so the page offers enrolment again.
      if (e instanceof ApiError && e.code === 'reenrol_required') void qc.invalidateQueries({ queryKey: ['checkin-status'] });
    } finally {
      cam.stop();
    }
  };

  const begin = async () => {
    setProblem(null);
    setResult(null);
    const ch = await post<{ id: string; nonce: string; steps: Step[] }>('/api/checkin/challenge');
    setSteps(ch.steps);
    setStepIdx(0);
    setPhase('challenge');
    await cam.start();
    let lm;
    try {
      lm = await loadLandmarker();
      setModelError(null);
    } catch {
      setModelError('The on-device face model could not load (offline, or blocked by a content filter).');
      return;
    }
    const started = performance.now();
    const passed: { step: Step; passedAt: number }[] = [];
    let idx = 0;
    let detect = stepDetector(ch.steps[0]!);
    let frames = 0;
    let lastTs = -1;
    // The latest frame where the person faced the camera; sent for identity instead of the last (turned) one.
    let frontal: { jpeg: string; at: number } | null = null;
    const tick = () => {
      const v = cam.videoRef.current;
      if (!v || v.readyState < 2) {
        loop.current = requestAnimationFrame(tick);
        return;
      }
      const ts = performance.now();
      if (ts - lastTs > 40) {
        lastTs = ts;
        const r = lm.detectForVideo(v, ts);
        const sig = readSignal(r);
        frames++;
        if (isFrontal(sig) && ts - (frontal?.at ?? -Infinity) > 400) frontal = { jpeg: captureJpeg(v), at: ts };
        setFramed(sig.framed && sig.faces === 1);
        if (sig.faces > 1) setProblem('Only one person should be in frame.');
        else setProblem(null);
        if (sig.framed && detect(sig)) {
          passed.push({ step: ch.steps[idx]!, passedAt: Math.round(ts - started) });
          idx++;
          setStepIdx(idx);
          if (idx >= ch.steps.length) {
            void submit(ch, { steps: passed, frames, durationMs: Math.round(ts - started) }, frontal?.jpeg);
            return;
          }
          detect = stepDetector(ch.steps[idx]!);
        }
      }
      loop.current = requestAnimationFrame(tick);
    };
    loop.current = requestAnimationFrame(tick);
  };

  /** DEVELOPMENT ONLY — when the model can't load and the server uses the mock matcher. */
  const simulate = async () => {
    const ch = await post<{ id: string; nonce: string; steps: Step[] }>('/api/checkin/challenge');
    setSteps(ch.steps);
    await new Promise((r) => setTimeout(r, 300));
    void submit(ch, { steps: ch.steps.map((st, i) => ({ step: st, passedAt: 900 + i * 900 })), frames: 48, durationMs: 900 + ch.steps.length * 900 });
  };

  if (!me.employee) return <Callout tone="info">Check-in is for employees. Your account isn’t linked to an employee profile.</Callout>;

  const ts = s?.timesheet;
  const reenrol = s?.enrolled?.current === false;
  return (
    <div>
      <PageHead
        eyebrow={`Check in · ${s?.office.name ?? ''}`}
        title={
          s?.next === 'out' && ts?.clockInAt ? (
            <>
              Heading <em>home?</em>
            </>
          ) : (
            <>
              Good to <em>see you.</em>
            </>
          )
        }
        lede="Your face is matched on our servers against the template you enrolled. Photos aren’t kept, and you can withdraw consent at any time."
      />
      {status.isLoading ? (
        <Skeleton h={360} />
      ) : !s?.consent ? (
        <Reveal className="grid-12">
          <Reveal.Item className="col-7">
            <Panel eyebrow="Before you start" title="Consent to face check-in">
              <div className="stack">
                <p className="muted">Under Indonesia’s Personal Data Protection Law (UU 27/2022), a face template is specific personal data. Here’s exactly what happens:</p>
                <ul className="list-plain">
                  {[
                    ['We store', 'a mathematical template of your face, encrypted. Not your photo.'],
                    ['We use it', 'only to confirm it’s you when you check in or out.'],
                    ['We record', 'the time, a match score, and your location if you allow it.'],
                    ['You can', 'withdraw consent any time; your template is then deleted, and you can use the fingerprint reader instead.'],
                  ].map(([a, b]) => (
                    <li key={a} className="mini-row" style={{ alignItems: 'start' }}>
                      <Icon name="check" width={16} height={16} style={{ color: 'var(--accent-text)', marginTop: 3 }} />
                      <span>
                        <strong style={{ fontWeight: 600 }}>{a}</strong> {b}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="row">
                  <Button variant="primary" icon="shield" onClick={consent}>
                    I agree
                  </Button>
                  <span className="faint" style={{ fontSize: 'var(--step--2)' }}>
                    Policy version face-2026-10
                  </span>
                </div>
              </div>
            </Panel>
          </Reveal.Item>
        </Reveal>
      ) : (
        <div className="checkin">
          {s.enrolled && (placeBlock === 'no-location' || placeBlock === 'off-network') ? (
            <div style={{ gridColumn: '1 / -1' }}>
              <Callout tone="warn" title={placeBlock === 'off-network' ? 'You’re not on the office network' : geo.state === 'denied' ? 'Location is blocked for this site' : geo.state === 'unsupported' ? 'This browser can’t share your location' : 'We can’t get your location'}>
                {placeBlock === 'off-network' ? (
                  <>Check-in for {s.office.name} only works from the office network. Connect to the office Wi-Fi (not mobile data), then try again.</>
                ) : s.office.hasNetworks ? (
                  <>
                    To check in at {s.office.name}, either <strong>allow location</strong> for this site in your browser’s settings so we can see you’re inside the office area, or <strong>connect to the office Wi-Fi</strong>. One of the two is enough.
                  </>
                ) : (
                  <>To check in at {s.office.name}, allow location for this site in your browser’s settings so we can see you’re inside the office area. This office doesn’t accept check-in by network.</>
                )}{' '}
                <button className="link" style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-text)' }} onClick={recheck}>
                  Try again
                </button>
              </Callout>
            </div>
          ) : null}
          <div className="stack">
            <div className="camera">
              <video ref={cam.videoRef} playsInline muted aria-label="Camera preview" />
              {cam.on ? <div className="camera__oval" data-state={framed ? 'ok' : 'wait'} /> : null}
              <AnimatePresence>
                {!cam.on && phase !== 'done' ? (
                  <motion.div className="camera__idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <Icon name="face" width={44} height={44} />
                    <span>{reenrol ? 'Face check-in was upgraded. Enrol again: it takes about five seconds.' : s.enrolled ? 'Your camera starts when you begin.' : 'First, enrol your face. It takes about five seconds.'}</span>
                  </motion.div>
                ) : null}
                {phase === 'challenge' && cam.on ? (
                  <motion.div className="camera__prompt" key={stepIdx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: EASE }}>
                    <span className="display">{steps[stepIdx] ? STEP_PROMPT[steps[stepIdx]!] : 'Hold still…'}</span>
                    <div className="steps">
                      {steps.map((st, i) => (
                        <span key={st} data-done={i < stepIdx} />
                      ))}
                    </div>
                  </motion.div>
                ) : null}
                {phase === 'enrolling' ? (
                  <motion.div className="camera__prompt" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <span className="display">Look straight at the camera</span>
                  </motion.div>
                ) : null}
                {phase === 'done' && result ? (
                  <motion.div className="camera__idle" style={{ background: 'radial-gradient(circle, rgb(201 242 77 / 0.12), transparent 60%)' }} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: EASE }}>
                    <motion.svg width="72" height="72" viewBox="0 0 72 72" fill="none">
                      <motion.circle cx="36" cy="36" r="33" stroke="var(--accent)" strokeWidth="2" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.8, ease: EASE }} />
                      <motion.path d="M23 37.5 32 46l17-19" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.5, delay: 0.5, ease: EASE }} />
                    </motion.svg>
                    <span className="display" style={{ fontSize: 'var(--step-3)', color: '#ece6da' }}>
                      Checked {result.direction} at {time(result.at, tz)}
                    </span>
                    {result.lateMinutes ? <span style={{ color: 'var(--clay)' }}>{minutes(result.lateMinutes)} after your shift started</span> : result.direction === 'in' ? <span>Right on time.</span> : <span>Have a good evening.</span>}
                    {result.placeCheck === 'network' ? <span style={{ fontSize: 'var(--step--1)', opacity: 0.8 }}>Confirmed by the office network</span> : null}
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
            {cam.error ? <Callout tone="bad">{cam.error}</Callout> : null}
            {problem ? <Callout tone="bad">{problem}</Callout> : null}
            {modelError ? (
              <Callout tone="warn" title="Liveness check unavailable.">
                {modelError}{' '}
                {s.matcher === 'mock' ? (
                  <>
                    This server runs the <strong>development matcher</strong>, so you can{' '}
                    <button className="link" style={{ border: 0, background: 'none', cursor: 'pointer', color: 'var(--accent-text)' }} onClick={simulate}>
                      simulate the challenge
                    </button>{' '}
                    to try the flow.
                  </>
                ) : (
                  'Use the fingerprint reader for now.'
                )}
              </Callout>
            ) : null}
          </div>
          <div className="stack">
            <div className="panel" style={{ padding: 28 }}>
              <span className="eyebrow">{s.office.name} · {tz === 'Asia/Jakarta' ? 'WIB' : 'WITA'}</span>
              <div className="big-clock num" style={{ marginTop: 10 }}>
                {time(now, tz)}
              </div>
              <div className="row row--wrap" style={{ marginTop: 18 }}>
                {ts?.shiftStartAt ? <Chip>Shift {time(ts.shiftStartAt, tz)}–{time(ts.shiftEndAt, tz)}</Chip> : <Chip>No shift today</Chip>}
                {ts?.clockInAt ? <Chip tone="ok">In {time(ts.clockInAt, tz)}</Chip> : null}
                {ts?.clockOutAt ? <Chip tone="plain">Out {time(ts.clockOutAt, tz)}</Chip> : null}
              </div>
              <hr className="rule" style={{ margin: '22px 0' }} />
              <div className="row" style={{ ['--gap' as string]: '10px', fontSize: 'var(--step--1)' }}>
                <Icon name="pin" width={17} height={17} className="faint" />
                {geo.state === 'denied' ? (
                  <span className="faint">Location is blocked</span>
                ) : geo.state === 'unsupported' ? (
                  <span className="faint">Location isn’t available in this browser</span>
                ) : !geo.loc ? (
                  <span className="faint">{geo.state === 'unavailable' ? 'Couldn’t get your location' : 'Finding your location…'}</span>
                ) : dist !== null ? (
                  <span>
                    {dist < 1000 ? `${Math.round(dist)} m` : `${(dist / 1000).toFixed(1)} km`} from the office{' '}
                    {inside ? <Chip tone="ok">Inside</Chip> : <Chip tone="bad">Outside</Chip>}
                  </span>
                ) : (
                  <span className="faint">Location acquired (±{Math.round(geo.loc.accuracy)} m)</span>
                )}
              </div>
              {s.office.hasNetworks ? (
                <div className="row" style={{ ['--gap' as string]: '10px', fontSize: 'var(--step--1)', marginTop: 8 }}>
                  <Icon name="building" width={17} height={17} className="faint" />
                  {onNetwork ? (
                    <span>
                      On the office network <Chip tone="ok">Allowed</Chip>
                    </span>
                  ) : (
                    <span className="faint">Not on the office network</span>
                  )}
                </div>
              ) : null}
              {s.enrolled && fenced && inside === false && !onNetwork ? (
                <p className="faint" style={{ fontSize: 'var(--step--1)', margin: '10px 0 0' }}>
                  Check-in from here will be refused.{s.office.hasNetworks ? ' At the office? Connect to its Wi-Fi.' : ''}
                </p>
              ) : null}
              <div style={{ marginTop: 24 }}>
                {!s.enrolled || reenrol ? (
                  <Button variant="primary" size="lg" icon="camera" loading={phase === 'enrolling'} onClick={enroll}>
                    {reenrol ? 'Enrol again' : 'Enrol my face'}
                  </Button>
                ) : phase === 'challenge' || phase === 'submitting' ? (
                  <Button
                    size="lg"
                    onClick={() => {
                      cancelAnimationFrame(loop.current);
                      cam.stop();
                      setPhase('idle');
                    }}
                    loading={phase === 'submitting'}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button variant="primary" size="lg" icon="face" onClick={begin} disabled={placeBlock !== null} title={placeBlock === 'waiting' ? 'Waiting for your location' : undefined}>
                    {placeBlock === 'waiting' ? 'Finding your location…' : phase === 'done' ? 'Check again' : s.next === 'out' && ts?.clockInAt ? 'Check out' : 'Check in'}
                  </Button>
                )}
              </div>
            </div>
            {s.enrolled ? (
              <p className="faint" style={{ fontSize: 'var(--step--2)' }}>
                Enrolled {new Date(s.enrolled.at).toLocaleDateString('en-GB')} · model {s.enrolled.model}
                {s.matcher === 'mock' ? ' · development matcher (not for production)' : ''} ·{' '}
                <button
                  className="link"
                  style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer', color: 'inherit' }}
                  onClick={async () => {
                    await post('/api/checkin/consent/revoke');
                    void qc.invalidateQueries({ queryKey: ['checkin-status'] });
                    toast({ title: 'Consent withdrawn', body: 'Your face template was deleted.' });
                  }}
                >
                  Withdraw consent
                </button>
              </p>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
